import { Modal } from 'flowbite-react';
import React from 'react';
import { HiX } from 'react-icons/hi';
import { Drawer } from 'vaul';
import { JobseekerDrivePosition } from 'types';
import { CompanyLogo, formatPositionTitle, formatSalary, PrimaryButton, useIsMobile } from './shared';

const Stat = ({ label, value, className = '' }: { label: string; value: string; className?: string }) => (
	<div className={`px-4 py-2.5 border-[#EEEEF5] ${className}`}>
		<p className="text-[12px] text-[#8A8BA3]">{label}</p>
		<p className="text-[14.5px] font-[600] text-[#111E2C] dark:text-white">{value}</p>
	</div>
);

const Details = ({ position }: { position: JobseekerDrivePosition }) => (
	<div className="space-y-6">
		<div className="grid grid-cols-2 md:grid-cols-4 rounded-[14px] border border-[#EEEEF5] overflow-hidden">
			<Stat label="Package" value={formatSalary(position.salary)} className="border-r border-b md:border-b-0" />
			<Stat label="Openings" value={String(position.vacancy ?? '-')} className="border-b md:border-b-0 md:border-r" />
			<Stat label="Rounds" value={String(position.numberOfRounds ?? '-')} className="border-r" />
			<Stat label="Location" value={position.workLocations?.map(location => location.split(',')[0].trim()).join(', ') || '-'} />
		</div>
		{position.description ? (
			<div>
				<h4 className="text-[16px] font-[700] text-[#111E2C] dark:text-white">About the role</h4>
				<p className="mt-1.5 text-[14.5px] leading-[24px] text-[#555770] dark:text-[#B0B8C4] whitespace-pre-line">{position.description}</p>
			</div>
		) : null}
		{position.skills.length ? (
			<div>
				<h4 className="text-[16px] font-[700] text-[#111E2C] dark:text-white">What they&apos;re looking for</h4>
				<div className="mt-2 flex flex-wrap gap-2">
					{position.skills.map(skill => (
						<span key={skill} className="px-3 py-1.5 rounded-full bg-[#F1F1F5] dark:bg-[#111827] text-[13.5px] text-[#3C3E55] dark:text-[#B0B8C4]">
							{skill}
						</span>
					))}
				</div>
			</div>
		) : null}
	</div>
);

const PositionDetailModal = ({
	position,
	onClose,
	onApply,
	canApply,
	actionLabel = 'Apply now',
	actionSecondary = false,
}: {
	position: JobseekerDrivePosition | null;
	onClose: () => void;
	onApply: (position: JobseekerDrivePosition) => void;
	canApply: boolean;
	actionLabel?: string;
	actionSecondary?: boolean;
}) => {
	const isMobile = useIsMobile();
	const showApply = !!position && canApply && !position.applicationStatus;

	// Keep the last position mounted while the sheet animates out.
	const lastPosition = React.useRef<JobseekerDrivePosition | null>(null);
	if (position) lastPosition.current = position;
	const shown = position ?? lastPosition.current;

	if (isMobile) {
		return (
			<Drawer.Root open={!!position} onOpenChange={open => !open && onClose()}>
				<Drawer.Portal>
					<Drawer.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]" />
					<Drawer.Content
						className="fixed inset-x-0 bottom-0 z-50 flex max-h-[92vh] flex-col rounded-t-[28px] bg-white dark:bg-[#1F2937] outline-none"
						style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
					>
						<Drawer.Title className="sr-only">{shown?.company.name} position details</Drawer.Title>
						<Drawer.Description className="sr-only">Details of the position</Drawer.Description>
						<div className="mx-auto mt-3 h-1.5 w-[44px] shrink-0 rounded-full bg-[#E3E3EC]" />
						{shown ? (
							<>
								<div className="flex items-center gap-3.5 px-5 pt-5 pb-4">
									<CompanyLogo name={shown.company.name} logo={shown.company.logo} url={shown.company.url} size={56} />
									<div className="min-w-0 flex-1">
										<h3 className="text-[17px] font-[700] leading-[22px] text-[#111E2C] dark:text-white">{shown.company.name}</h3>
										<p className="mt-0.5 text-[13.5px] text-[#555770] dark:text-[#B0B8C4]">{formatPositionTitle(shown.title)}</p>
									</div>
									<button aria-label="Close" onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F1F1F5] dark:bg-[#111827] text-[#111E2C] dark:text-white">
										<HiX className="h-[18px] w-[18px]" />
									</button>
								</div>
								<div className="flex-1 overflow-y-auto px-5 pb-6" data-vaul-no-drag>
									<Details position={shown} />
								</div>
								<div className="shrink-0 border-t border-[#EEEEF5] px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
									{showApply ? (
										actionSecondary ? (
											<button
												onClick={() => onApply(shown)}
												className="w-full rounded-[12px] border border-[#E3E3EC] bg-white dark:bg-[#111827] py-4 text-[17px] font-[600] text-[#111E2C] dark:text-white"
											>
												{actionLabel}
											</button>
										) : (
											<PrimaryButton className="w-full !py-4 !text-[17px]" onClick={() => onApply(shown)}>
												{actionLabel}
											</PrimaryButton>
										)
									) : (
										<button onClick={onClose} className="w-full rounded-[12px] bg-[#F1F1F5] dark:bg-[#111827] py-4 text-[17px] font-[600] text-[#111E2C] dark:text-white">
											Close
										</button>
									)}
								</div>
							</>
						) : null}
					</Drawer.Content>
				</Drawer.Portal>
			</Drawer.Root>
		);
	}

	return (
		<Modal show={!!position} onClose={onClose} size="3xl" dismissible>
			{position ? (
				<>
					<div className="flex items-center gap-4 p-6 border-b border-[#EEEEF5]">
						<CompanyLogo name={position.company.name} logo={position.company.logo} url={position.company.url} size={56} />
						<div className="flex-1 min-w-0">
							<h3 className="text-[20px] font-[700] text-[#111E2C] dark:text-white">{position.company.name}</h3>
							<p className="text-[15px] text-[#555770]">{formatPositionTitle(position.title)}</p>
						</div>
						<button aria-label="Close" onClick={onClose} className="w-9 h-9 rounded-full bg-[#F1F1F5] text-[#555770]">
							✕
						</button>
					</div>
					<div className="p-6 max-h-[60vh] overflow-y-auto">
						<Details position={position} />
					</div>
					<div className="flex justify-end gap-3 p-4 bg-[#F6F6FA] dark:bg-[#111827] rounded-b-lg">
						<button onClick={onClose} className="rounded-[12px] bg-white border border-[#EEEEF5] px-6 py-3 font-[600] text-[#111E2C]">
							Close
						</button>
						{showApply ? <PrimaryButton onClick={() => onApply(position)}>{actionLabel}</PrimaryButton> : null}
					</div>
				</>
			) : null}
		</Modal>
	);
};

export default PositionDetailModal;
